import { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase/config';
import { useAuth } from './AuthContext';
import { playGenericNotificationBeep, playLeadNotificationChime } from '../utils/notificationSound';

/**
 * User notification document structure
 */
export interface UserNotification {
  id: string;
  type:
    | 'whatsapp_reply'
    | 'new_lead_assigned'
    | 'system'
    | 'support_new_item'
    | 'support_staff_reply'
    | 'support_status_change'
    | 'support_user_reply'
    | 'support_assigned'
    | string;
  title: string;
  body: string;
  /** Legacy text field: some client writers (e.g. CallCentreCalendarBooking) set `message` instead of `body`. */
  message?: string;
  data: {
    contactId?: string;
    contactName?: string;
    contactPhone?: string;
    contactEmail?: string;
    partnerId?: string;
    campaignName?: string;
    developmentId?: string;
    developmentName?: string;
    leadScore?: number;
    leadQuality?: string;
    source?: string;
    /** Support notifications: app-relative deep link, e.g. /my-tickets?ticket=abc */
    path?: string;
    /** Support notifications: source doc id */
    itemId?: string;
    [key: string]: any;
  };
  read: boolean;
  readAt?: Timestamp;
  createdAt: Timestamp;
  expiresAt?: Timestamp;
}

type LeadNotificationCallback = (notification: UserNotification) => void;

interface NotificationContextType {
  notifications: UserNotification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  registerLeadNotificationCallback: (cb: LeadNotificationCallback) => void;
  unregisterLeadNotificationCallback: () => void;
  /** When true, popup dispatch and notification sounds are suppressed on this
   *  device. Notifications still arrive in the bell and count as unread. */
  muted: boolean;
  toggleMuted: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

/**
 * NotificationProvider - Provides real-time notification state to the app
 */
const muteStorageKey = (userId: string) => `pc_notifications_muted:${userId}`;

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { userProfile } = useAuth();
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Mute-all toggle — per user, per device (localStorage). Read via a ref so
  // the long-lived onSnapshot closure always sees the current value.
  const [muted, setMuted] = useState(false);
  const mutedRef = useRef(false);
  useEffect(() => {
    const stored = userProfile?.id
      ? localStorage.getItem(muteStorageKey(userProfile.id)) === 'true'
      : false;
    setMuted(stored);
    mutedRef.current = stored;
  }, [userProfile?.id]);

  const toggleMuted = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      mutedRef.current = next;
      if (userProfile?.id) {
        try {
          localStorage.setItem(muteStorageKey(userProfile.id), String(next));
        } catch {
          // storage unavailable — in-memory toggle still works for the session
        }
      }
      return next;
    });
  }, [userProfile?.id]);

  // Lead notification callback ref — used by LeadNotificationStack
  const leadCallbackRef = useRef<LeadNotificationCallback | null>(null);

  // True once the FIRST snapshot of the current subscription has been
  // processed. Firestore's initial snapshot reports every doc as `added`,
  // so dispatching popups/sounds before this flag is set replays the whole
  // unread backlog (up to 50 docs) as a popup wall — which is exactly what
  // happened on impersonation/user switches. A ref (not state) for the same
  // reason as mutedRef: the long-lived onSnapshot closure must always see
  // the current value.
  const initialSnapshotHandledRef = useRef(false);

  const registerLeadNotificationCallback = useCallback((cb: LeadNotificationCallback) => {
    leadCallbackRef.current = cb;
  }, []);

  const unregisterLeadNotificationCallback = useCallback(() => {
    leadCallbackRef.current = null;
  }, []);

  // Subscribe to user notifications
  useEffect(() => {
    if (!userProfile?.id) {
      setNotifications([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);
    // Re-arm the initial-snapshot skip for the new subscription (user
    // switch / impersonation) so the incoming backlog never pops.
    initialSnapshotHandledRef.current = false;

    const q = query(
      collection(db, 'userNotifications'),
      where('userId', '==', userProfile.id),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const notifs = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        })) as UserNotification[];

        // Check for new unread notifications (play sound / dispatch popup)
        const newUnread = snapshot.docChanges()
          .filter(change => change.type === 'added' && !change.doc.data().read);

        // Skip the initial snapshot entirely — only docs that arrive AFTER
        // it are genuinely new. (The old guard compared against a stale
        // `notifications` closure: it both replayed the backlog on
        // re-subscribe and, in steady state, often never fired at all.)
        if (initialSnapshotHandledRef.current && newUnread.length > 0) {
          const leadNotifs: UserNotification[] = [];
          let hasOtherNotifs = false;

          for (const change of newUnread) {
            const data = change.doc.data();
            if (data.type === 'new_lead_assigned') {
              leadNotifs.push({ id: change.doc.id, ...data } as UserNotification);
            } else {
              hasOtherNotifs = true;
            }
          }

          // Muted: skip popups and sounds entirely — notifications still land
          // in the bell and count as unread.
          if (!mutedRef.current) {
            // Dispatch lead notifications to popup callback + ONE chime for
            // the whole batch (the popup no longer chimes per card).
            if (leadNotifs.length > 0) {
              if (leadCallbackRef.current) {
                for (const notif of leadNotifs) {
                  leadCallbackRef.current(notif);
                }
              }
              playLeadNotificationChime();
            }

            // Play generic beep for non-lead notifications
            if (hasOtherNotifs) {
              playGenericNotificationBeep();
            }
          }
        }

        initialSnapshotHandledRef.current = true;
        setNotifications(notifs);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching notifications:', err);
        setError('Failed to load notifications');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userProfile?.id]);

  // Calculate unread count
  const unreadCount = notifications.filter(n => !n.read).length;

  // Mark single notification as read
  const markAsRead = useCallback(async (id: string) => {
    try {
      await updateDoc(doc(db, 'userNotifications', id), {
        read: true,
        readAt: Timestamp.now()
      });
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      const unread = notifications.filter(n => !n.read);
      await Promise.all(unread.map(n => markAsRead(n.id)));
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  }, [notifications, markAsRead]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        error,
        markAsRead,
        markAllAsRead,
        registerLeadNotificationCallback,
        unregisterLeadNotificationCallback,
        muted,
        toggleMuted
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

/**
 * Hook to access notification context
 */
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};
